import { computed, onMounted, ref, type ComputedRef } from 'vue'

import { definitionsMetadataApi, type DefinitionsMetadataApi } from '../api/DefinitionsMetadataApi'
import { SaveCustomMetricFlow } from '../flows/SaveCustomMetricFlow'
import type { CustomMetric, CustomMetricDraft } from '../models/metric.types'
import { useDefinitionsStore } from '../stores/definitionsStore'

import { useWorkspace } from '@/features/account'
import {
  invalidateMetricCatalog,
  removeRuntimeMetricDefinition,
  useLevelMetadata,
  useMetricCatalog,
  type ReportLevel,
} from '@/features/reports'
import type { UiAsyncState } from '@/shared/ui'

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function mergeSources(
  remote: readonly CustomMetric[],
  attributes: Record<string, unknown>,
): CustomMetric[] {
  return remote.map((metric) => {
    const override = record(attributes[metric.metric_name])
    const expression =
      typeof override.expression === 'string'
        ? override.expression
        : typeof metric.expression === 'string'
          ? metric.expression
          : metric.formula
    return { ...metric, ...override, expression } as CustomMetric
  })
}

export interface UseCustomMetrics {
  metrics: ComputedRef<CustomMetric[]>
  levels: ComputedRef<ReportLevel[]>
  state: ComputedRef<UiAsyncState>
  error: ComputedRef<unknown>
  pending: ComputedRef<boolean>
  reload: () => Promise<void>
  save: (draft: CustomMetricDraft, level: ReportLevel, original?: CustomMetric) => Promise<boolean>
  remove: (metric: CustomMetric) => Promise<boolean>
}

export function useCustomMetrics(
  api: DefinitionsMetadataApi = definitionsMetadataApi,
): UseCustomMetrics {
  const workspace = useWorkspace()
  const levels = useLevelMetadata()
  const catalog = useMetricCatalog()
  const store = useDefinitionsStore()
  const loading = ref(false)
  const pending = ref(false)
  const loadError = ref<unknown>(null)

  async function reload(): Promise<void> {
    const workspaceId = workspace.workspaceId.value
    if (workspaceId === undefined) return
    loading.value = true
    loadError.value = null
    try {
      const remote = await api.listCustomMetrics(workspaceId)
      const attribute = record(workspace.getAttribute('workspace_custom_metrics')?.value)
      store.setCustomMetrics(mergeSources(remote, attribute))
      store.markLoaded(workspaceId)
    } catch (cause) {
      loadError.value = cause
    } finally {
      loading.value = false
    }
  }

  async function save(
    draft: CustomMetricDraft,
    level: ReportLevel,
    original?: CustomMetric,
  ): Promise<boolean> {
    const workspaceId = workspace.workspaceId.value
    if (workspaceId === undefined) return false
    pending.value = true
    loadError.value = null
    try {
      const flow = new SaveCustomMetricFlow({
        api,
        refreshAttributes: workspace.refreshAttributes,
        invalidateCatalog: (metric, previousField) =>
          invalidateMetricCatalog({
            workspaceId,
            field: metric.metric_name,
            ...(previousField ? { previousField } : {}),
            partitionName: metric.level,
            header: catalog.metricLabel(metric.metric_name),
            format: metric.format,
            changeType: metric.change_type,
          }),
      })
      const result = await flow.save(
        draft,
        original ? 'update' : 'create',
        workspaceId,
        level.tableMetrics.map((metric) => metric.field),
        original,
      )
      if (!result.ok) {
        loadError.value = result.error
        return false
      }
      await reload()
      return true
    } catch (cause) {
      loadError.value = cause
      return false
    } finally {
      pending.value = false
    }
  }

  async function remove(metric: CustomMetric): Promise<boolean> {
    const workspaceId = workspace.workspaceId.value
    if (workspaceId === undefined || metric.id === undefined) return false
    pending.value = true
    loadError.value = null
    try {
      await api.deleteCustomMetric(metric.id)
      await workspace.refreshAttributes()
      removeRuntimeMetricDefinition(workspaceId, metric.metric_name)
      await reload()
      return true
    } catch (cause) {
      loadError.value = cause
      return false
    } finally {
      pending.value = false
    }
  }

  onMounted(() => void reload())
  return {
    metrics: computed(() => store.customMetrics),
    levels: levels.levels,
    state: computed(() =>
      loading.value
        ? 'loading'
        : loadError.value
          ? 'error'
          : store.customMetrics.length
            ? 'ready'
            : 'empty',
    ),
    error: computed(() => loadError.value),
    pending: computed(() => pending.value),
    reload,
    save,
    remove,
  }
}
